import { MutationCache, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ThemeProvider } from "next-themes";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router";
import { toast } from "sonner";

import { BrandingSync } from "@/components/branding-sync";
import { GuestRoute } from "@/components/guest-route";
import { AppLayout } from "@/components/layout/app-layout";
import { ProtectedRoute } from "@/components/protected-route";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Toaster } from "@/components/ui/sonner";
import { getErrorMessage } from "@/lib/error-message";
import { persister } from "@/lib/query-persister";
import { AccountSettings } from "@/pages/account-settings";
import { ActivityLogList } from "@/pages/activity-logs/activity-log-list";
import { CashReportPage } from "@/pages/cash-report/cash-report";
import { Dashboard } from "@/pages/dashboard";
import { ExpenseFormPage } from "@/pages/expenses/expense-form-page";
import { ExpenseList } from "@/pages/expenses/expense-list";
import { GrossProfitPage } from "@/pages/gross-profit";
import { Login } from "@/pages/login";
import { Products } from "@/pages/master/products";
import { RawMaterials } from "@/pages/master/raw-materials";
import { Riders } from "@/pages/master/riders";
import { UnitConversions } from "@/pages/master/unit-conversions";
import { Uoms } from "@/pages/master/uoms";
import { OpeningStockListPage } from "@/pages/opening-stocks/opening-stock-list";
import { OpeningStockSessionPage } from "@/pages/opening-stocks/opening-stock-session-page";
import { ProductionList } from "@/pages/production";
import { ReceivableList } from "@/pages/receivables/receivable-list";
import { RepackingList } from "@/pages/repacking";
import { RiderPayrollList } from "@/pages/rider-payroll/rider-payroll-list";
import { RiderPendingPaymentList } from "@/pages/rider-sales/rider-pending-payment-list";
import { RiderSaleList } from "@/pages/rider-sales/rider-sale-list";
import { RiderStockList } from "@/pages/rider-sales/rider-stock-list";
import { SaleList } from "@/pages/sales/sale-list";
import {
  CurrentWorkspaceSettingsPage,
  NumberSequencePage,
} from "@/pages/settings/current-workspace";
import { NotificationsPage } from "@/pages/settings/notifications";
import { RoleForm } from "@/pages/settings/roles/role-form";
import { RoleList } from "@/pages/settings/roles/role-list";
import { UserList } from "@/pages/settings/users/user-list";
import { BonusRulesPage } from "@/pages/settings/workspaces/bonus-rules-page";
import { WorkspaceList } from "@/pages/settings/workspaces/workspace-list";
// Disabled for now — depends on notification_channel (see rest.ts).
// import { WorkspaceNotificationsPage } from "@/pages/settings/workspaces/workspace-notifications-page";
import { StockAdjustmentList } from "@/pages/stock-adjustments";
import { StockOpnameList, StockOpnameSessionPage } from "@/pages/stock-opname";
import { StockList } from "@/pages/stocks";
import { registerPwa } from "@/register-sw";
import "./index.css";

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

const router = createBrowserRouter([
  {
    path: "/login",
    element: <GuestRoute />,
    children: [{ index: true, element: <Login /> }],
  },
  {
    path: "/",
    element: <ProtectedRoute />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <AppLayout />,
        errorElement: <ErrorBoundary />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: "dashboard", element: <Dashboard /> },
          { path: "master/products", element: <Products /> },
          { path: "master/raw-materials", element: <RawMaterials /> },
          { path: "master/uoms", element: <Uoms /> },
          { path: "master/unit-conversions", element: <UnitConversions /> },
          { path: "master/riders", element: <Riders /> },
          { path: "sales", element: <SaleList /> },
          { path: "receivables", element: <ReceivableList /> },
          { path: "expenses", element: <ExpenseList /> },
          { path: "expenses/new", element: <ExpenseFormPage /> },
          { path: "expenses/:id/edit", element: <ExpenseFormPage /> },
          { path: "cash-report", element: <CashReportPage /> },
          { path: "gross-profit", element: <GrossProfitPage /> },
          { path: "rider-sales", element: <RiderSaleList /> },
          { path: "rider-stocks", element: <RiderStockList /> },
          {
            path: "rider-pending-payment",
            element: <RiderPendingPaymentList />,
          },
          { path: "rider-payrolls", element: <RiderPayrollList /> },
          { path: "stocks", element: <StockList /> },
          { path: "opening-stocks", element: <OpeningStockListPage /> },
          { path: "opening-stocks/new", element: <OpeningStockSessionPage /> },
          { path: "opening-stocks/:id", element: <OpeningStockSessionPage /> },
          {
            path: "stock-opname",
            element: <Navigate to="/stock-opname/raw-materials" replace />,
          },
          {
            path: "stock-opname/raw-materials",
            element: <StockOpnameList type="raw_material" />,
          },
          {
            path: "stock-opname/products",
            element: <StockOpnameList type="product" />,
          },
          {
            path: "stock-opname/sessions/:opnameId",
            element: <StockOpnameSessionPage />,
          },
          { path: "stock-adjustments", element: <StockAdjustmentList /> },
          { path: "repacking", element: <RepackingList /> },
          { path: "production", element: <ProductionList /> },
          { path: "activity-logs", element: <ActivityLogList /> },
          {
            path: "settings/workspace",
            element: <CurrentWorkspaceSettingsPage />,
          },
          {
            path: "settings/workspace/gaji-bonus-rider",
            element: <BonusRulesPage />,
          },
          // Disabled for now — depends on notification_channel (see rest.ts).
          // {
          //   path: "settings/workspace/notifications",
          //   element: <WorkspaceNotificationsPage />,
          // },
          {
            path: "settings/workspace/number-sequence",
            element: <NumberSequencePage />,
          },
          { path: "settings/workspaces", element: <WorkspaceList /> },
          {
            path: "settings/workspaces/:id/gaji-bonus-rider",
            element: <BonusRulesPage />,
          },
          {
            path: "settings/workspaces/:id/number-sequence",
            element: <NumberSequencePage />,
          },
          // Disabled for now — depends on notification_channel (see rest.ts).
          // {
          //   path: "settings/workspaces/:id/notifications",
          //   element: <WorkspaceNotificationsPage />,
          // },
          { path: "settings/account", element: <AccountSettings /> },
          { path: "settings/roles", element: <RoleList /> },
          { path: "settings/roles/new", element: <RoleForm /> },
          { path: "settings/roles/:id/edit", element: <RoleForm /> },
          { path: "settings/users", element: <UserList /> },
          {
            path: "settings/workspace/users",
            element: <UserList scoped />,
          },
          { path: "settings/notifications", element: <NotificationsPage /> },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/dashboard" replace />,
  },
]);

const queryClient = new QueryClient({
  defaultOptions: { queries: { gcTime: ONE_DAY_MS } },
  mutationCache: new MutationCache({
    onError: (error) => toast.error(getErrorMessage(error)),
  }),
});

registerPwa();

const elem = document.getElementById("root");
if (!elem) throw new Error("Missing #root element");

const app = (
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: ONE_DAY_MS }}
    >
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        disableTransitionOnChange
      >
        <BrandingSync />
        <RouterProvider router={router} />
        <Toaster />
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  </StrictMode>
);

createRoot(elem).render(app);
